import {
  operatorPascaNavigations,
  superAdminNavigations,
  verifikatorPascaNavigations,
} from "@/constants";
import { useTahun } from "@/hooks/useTahun";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { cn } from "@/lib/utils";
import { Option } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { HiMenuAlt2, HiOutlineArrowNarrowLeft } from "react-icons/hi";
import { HiOutlineDocumentText, HiUser } from "react-icons/hi2";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Modal from "./Modal";
import { AnimatedThemeToggler } from "./ui/animated-theme-toggler";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

function generateTahunOptions() {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 5;
  const endYear = currentYear + 5;

  const options = [];

  for (let year = startYear; year <= endYear; year++) {
    options.push({
      label: year.toString(),
      value: year,
    });
  }

  return options;
}

const getListTahunPeriode = async () => {
  const response = await api.get("/master/tahun-periode");
  return response.data?.data;
};

export default function Header() {
  const { user, logoutUser } = useUser();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { selectedTahun, setSelectedTahun } = useTahun();
  const tahunOptions = generateTahunOptions();

  const [showListMenu, setShowListMenu] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openTahun, setOpenTahun] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMenu, setSelectedMenu] = useState<{
    name: string;
    path: string | null;
  } | null>(null);

  const isMultiRole = user?.jenis_user && user?.jenis_user.length > 1;
  const isSuperAdmin =
    user?.jenis_user.includes("superadmin") && pathname.startsWith("/admin");
  const isPimpinan =
    user?.jenis_user.includes("pimpinan") && pathname.startsWith("/pimpinan");
  const isOperatorPasca =
    user?.jenis_user.includes("operator_pasca") &&
    pathname.startsWith("/operator-pasca");
  const isVerifikatorPasca =
    user?.jenis_user.includes("verifikator_pasca") &&
    pathname.startsWith("/verifikator-pasca");

  const listMenu = isSuperAdmin
    ? superAdminNavigations
    : isOperatorPasca
      ? operatorPascaNavigations
      : isVerifikatorPasca
        ? verifikatorPascaNavigations
        : [];

  const filteredListMenu = listMenu
    .map((section) => ({
      ...section,
      menus: section.menus
        .map((menu) => ({
          ...menu,
          submenus:
            menu.submenus &&
            menu.name.toLowerCase().includes(searchQuery.toLowerCase())
              ? menu.submenus
              : menu.submenus?.filter((submenu) =>
                  submenu.name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
                ),
        }))
        .filter(
          (menu) =>
            menu.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (menu.submenus && menu.submenus.length > 0),
        ),
    }))
    .filter((section) => section.menus.length > 0);

  const { data: optionsTahunPeriode } = useQuery({
    queryKey: ["listTahunPeriode"],
    queryFn: getListTahunPeriode,
    select: (data) => {
      return data?.map((value: string) => {
        return { label: value, value: Number(value) };
      });
    },
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "/") setShowListMenu(true);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (
      !filteredListMenu?.length ||
      !filteredListMenu[0]?.menus?.length ||
      selectedMenu
    )
      return;

    const firstMenu = filteredListMenu[0].menus[0];

    if (firstMenu?.path === null && firstMenu?.submenus?.length) {
      setSelectedMenu({
        name: firstMenu.submenus[0]?.name,
        path: firstMenu.submenus[0]?.path,
      });
    } else {
      setSelectedMenu({
        name: firstMenu?.name,
        path: firstMenu?.path,
      });
    }
  }, [searchQuery, filteredListMenu]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Flatten menu structure into a single ordered list
      const flatMenuList: { name: string; path: string | null }[] = [];

      filteredListMenu?.forEach((section) => {
        section.menus.forEach((menu) => {
          flatMenuList.push({ name: menu.name, path: menu.path });

          if (menu.submenus && menu.submenus.length > 0) {
            menu.submenus.forEach((submenu) => {
              flatMenuList.push({ name: submenu.name, path: submenu.path });
            });
          }
        });
      });

      if (flatMenuList.length === 0) return;

      const currentIndex = flatMenuList.findIndex(
        (item) => item.name === selectedMenu?.name,
      );

      if (event.key === "ArrowDown") {
        const nextIndex = (currentIndex + 1) % flatMenuList.length;
        setSelectedMenu(flatMenuList[nextIndex]);
      } else if (event.key === "ArrowUp") {
        const prevIndex =
          (currentIndex - 1 + flatMenuList.length) % flatMenuList.length;
        setSelectedMenu(flatMenuList[prevIndex]);
      } else if (event.key === "Enter" && showListMenu && selectedMenu?.path) {
        setShowListMenu(false);
        // Pindah ke route berdasarkan selectedMenu.path
        navigate(selectedMenu.path);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedMenu, filteredListMenu, navigate]);

  return (
    <header className="sticky top-3 bg-muted/10 backdrop-blur-sm mb-5 flex flex-wrap gap-3 justify-between print:hidden z-50">
      {!isPimpinan ? (
        <div className="flex items-center gap-3">
          <div
            className="relative py-2 px-3 rounded-full bg-secondary text-black/70 dark:bg-slate-900 dark:text-white dark:border dark:border-slate-800 h-full w-[20rem] cursor-pointer"
            onClick={() => setShowListMenu(true)}
          >
            <span>Cari menu...</span>
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <button className="py-2 px-2 w-8 h-8 bg-black/80 dark:bg-white/80 dark:text-black text-white font-semibold text-xs rounded-full">
                /
              </button>
            </div>
          </div>
          <h1 className="font-bold lg:block hidden">{pathname}</h1>
        </div>
      ) : (
        <Link to={"/"} className="font-semibold text-xl">
          <Button variant={"outline"} className="flex items-center gap-3">
            <HiOutlineArrowNarrowLeft />
            Beranda
          </Button>
        </Link>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <AnimatedThemeToggler />
        <Popover open={openTahun} onOpenChange={setOpenTahun}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={openTahun}
              className="w-[200px] justify-between"
            >
              {selectedTahun
                ? optionsTahunPeriode?.find(
                    (option: Option) =>
                      Number(option.value) === Number(selectedTahun?.value),
                  )?.label
                : "Pilih tahun..."}
              <ChevronsUpDown className="opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0">
            <Command>
              <CommandInput placeholder="Cari tahun..." className="h-9" />
              <CommandList>
                <CommandEmpty>Tahun tidak ditemukan</CommandEmpty>
                <CommandGroup>
                  {optionsTahunPeriode?.map((option: Option) => (
                    <CommandItem
                      key={option.value}
                      value={String(option.value)}
                      onSelect={(currentValue) => {
                        const selectedOption = tahunOptions.find(
                          (item) => Number(item.value) === Number(currentValue),
                        );
                        if (selectedOption) {
                          setSelectedTahun(selectedOption);
                        }
                        setOpenTahun(false);
                      }}
                    >
                      {option.label}
                      <Check
                        className={cn(
                          "ml-auto",
                          selectedTahun?.value == Number(option.value)
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className="flex gap-3">
                {user.nama_lengkap} <HiUser />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isMultiRole && (
                <DropdownMenuItem onClick={() => navigate("/global/dashboard")}>
                  Global Dashboard
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => {
                  logoutUser();
                  navigate("/login");
                }}
              >
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Modal show={showListMenu} onClose={() => setShowListMenu(false)}>
        <div className="mb-5">
          <input
            placeholder="Search..."
            className="py-3 px-3 w-full border-b focus:border-b-primary focus:outline-none 
  bg-transparent 
  text-slate-900 dark:text-slate-100 
  border-slate-300 dark:border-slate-700 
  placeholder:text-slate-400 dark:placeholder:text-slate-500"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
        <div>
          <div className="flex flex-col gap-y-3">
            {filteredListMenu?.map((section) => (
              <div key={section.title}>
                <h1 className="uppercase text-xs font-bold">{section.title}</h1>
                {section.menus?.map((menu, index) => {
                  const isOpen = openMenu === menu.name;
                  const isHovered = selectedMenu?.name == menu.name;

                  return (
                    <div key={index}>
                      <div
                        onClick={() => {
                          const isHasSubmenus = menu.path === null;
                          if (isHasSubmenus)
                            setOpenMenu(isOpen ? null : menu.name);
                          else {
                            setShowListMenu(false);
                            setSearchQuery("");
                            navigate(menu.path || "#");
                          }
                        }}
                        className={`p-2 flex items-center justify-between hover:bg-secondary rounded-xl cursor-pointer ${
                          isHovered ? "bg-secondary" : ""
                        }`}
                      >
                        <div
                          className="flex items-center gap-5"
                          onMouseEnter={() =>
                            setSelectedMenu({
                              name: menu.name,
                              path: menu.path,
                            })
                          }
                        >
                          <HiOutlineDocumentText />
                          <span>{menu.name}</span>
                        </div>
                        {menu.submenus && (
                          <span className="text-sm">
                            <FiChevronRight
                              className={`${
                                isOpen ? "rotate-90" : ""
                              } duration-300`}
                            />
                          </span>
                        )}
                      </div>

                      {isOpen && menu.submenus && menu.submenus.length > 0 && (
                        <div className="ml-6 mt-1">
                          {menu.submenus.map((submenu, subIndex) => {
                            const isHovered =
                              selectedMenu?.name === submenu.name;

                            return (
                              <div
                                key={subIndex}
                                className={`p-2 flex items-center gap-5 text-sm hover:bg-secondary rounded-xl cursor-pointer ${
                                  isHovered ? "bg-secondary" : ""
                                }`}
                                onMouseEnter={() =>
                                  setSelectedMenu({
                                    name: submenu.name,
                                    path: submenu.path,
                                  })
                                }
                                onClick={() => {
                                  setShowListMenu(false);
                                  setSearchQuery("");
                                  navigate(submenu.path);
                                }}
                              >
                                <HiMenuAlt2 /> {submenu.name}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </header>
  );
}
